/**
 * Google OAuth Button Component
 *
 * A button styled according to Google's branding guidelines for OAuth.
 * - White background with border
 * - Google "G" logo on the left
 * - "Continue with Google" text
 *
 * @see https://developers.google.com/identity/branding-guidelines
 */

import * as React from "react";

interface GoogleOAuthButtonProps {
  /** Click handler - typically navigates to OAuth endpoint */
  onClick?: () => void;
  /** Whether the button is in a loading state */
  isLoading?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** API base URL for OAuth endpoint */
  apiBaseUrl?: string;
  /** Return URL after successful authentication */
  returnUrl?: string;
}

/**
 * Google "G" Logo SVG
 * Official Google logo colors and proportions
 */
function GoogleLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9.003 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.482 18 9.003 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.712c-.18-.54-.282-1.117-.282-1.71 0-.593.102-1.17.282-1.71V4.96H.957C.347 6.175 0 7.55 0 9.002c0 1.452.348 2.827.957 4.042l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.002 0 5.48 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
      />
    </svg>
  );
}

/**
 * Loading Spinner for button loading state
 */
function LoadingSpinner({ className }: { className?: string }) {
  return (
    <svg
      className={`animate-spin ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function GoogleOAuthButton({
  onClick,
  isLoading = false,
  disabled = false,
  className = "",
  apiBaseUrl = "",
  returnUrl,
}: GoogleOAuthButtonProps) {
  const [loading, setLoading] = React.useState(isLoading);

  const handleClick = () => {
    if (disabled || loading) return;

    // Call onClick handler if provided (for custom behavior like analytics)
    onClick?.();

    // Always set loading and navigate to OAuth endpoint
    setLoading(true);

    // Navigate to Google OAuth endpoint
    // If apiBaseUrl is provided (absolute URL), use it directly; otherwise use relative path
    const baseUrl = apiBaseUrl || "";
    const oauthPath = `${baseUrl}/api/auth/google`;
    const url = apiBaseUrl ? new URL(oauthPath) : new URL(oauthPath, window.location.origin);
    if (returnUrl) {
      url.searchParams.set("returnUrl", returnUrl);
    }
    window.location.href = url.toString();
  };

  const isDisabled = disabled || loading;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-label="Continue with Google"
      className={`
        relative flex w-full items-center justify-center gap-3
        rounded-2xl border-solid border-[0.5px] border-input bg-white
        px-4 py-2.5 text-sm font-medium text-foreground
        shadow-xs cursor-pointer
        transition-colors
        hover:bg-gray-50 hover:shadow-xs
        focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
        disabled:cursor-not-allowed disabled:opacity-50
        ${className}
      `}
    >
      {loading ? (
        <>
          <LoadingSpinner className="h-5 w-5 text-gray-400" />
          <span>Connecting to Google...</span>
        </>
      ) : (
        <>
          <GoogleLogo className="h-5 w-5" />
          <span>Continue with Google</span>
        </>
      )}
    </button>
  );
}
